/*!
 * preProcessPattern.js — a pattern fill becomes propagate() members at load
 * Part of oscillaScore modular architecture
 * © 2025 Rob Canning — GPLv3
 *
 * An Inkscape pattern fill (polka dots, stripes, a tile of your own) is a
 * <pattern> in defs that the shape merely refers to: nothing in it is an
 * element, so nothing in it can carry a cue. Put a propagate() template on
 * the shape anyway --
 *
 *     <ellipse id="propagate(scale(values:[1,${1}], dur:${2}, mode:alternate, trig:playhead, uid:pd), rnd(0.6,1.6), rnd(0.8,2.5))"
 *              style="fill:url(#pattern123)" .../>
 *
 * -- and at load the tile is laid out for real over the shape: a <g> of
 * copies placed exactly where the pattern drew them, clipped to the shape,
 * inserted right after it. The group takes the propagate() id and the
 * shape a plain one, so preProcessPropagate then expands the group like any
 * other; the shape keeps its stroke and loses the fill for the session.
 * Nothing is written to the score: change the pattern's scale or spacing
 * with Inkscape's pattern tool and the dots follow on the next load. The
 * Inkscape extension "Expand Pattern Fill to Objects" does the same thing
 * once, into the file, for dots you want to hand-tune.
 *
 * Geometry (patternUnits="userSpaceOnUse", the only kind Inkscape writes):
 * the tile lattice lives in the shape's own user space, tile (i, j) at
 * (i*width, j*height), the whole lattice through patternTransform. The group
 * gets the shape's transform, so its children live in that same space; each
 * copy gets patternTransform x translate(i*w, j*h) x its own. Inkscape's
 * stock patterns are reached through a second <pattern> that carries only an
 * href and a patternTransform; attributes resolve down that chain the way
 * SVG does (nearest wins, children from the first pattern that has any).
 */

// Where a propagate() template lives, and how to read it. propagate() calls
// expandPatternFills() first, so this import is circular — safe because both
// are used at call time, never while the modules are evaluating.
import { PROPAGATE_SELECTOR, readPropagateSource } from "./preProcessPropagate.js";

const SVG_NS = "http://www.w3.org/2000/svg";
const GRAPHIC = new Set(["circle", "ellipse", "rect", "path", "polygon", "polyline", "line", "g", "use", "text", "image"]);

function patternOf(el) {
    if (typeof getComputedStyle !== "function" || !el?.getAttribute) return null;   // not a browser DOM (tests)
    let fill = "";
    try { fill = getComputedStyle(el).fill || ""; } catch { /* detached or fake element */ }
    fill = (fill || el.style?.fill || el.getAttribute("fill") || "").trim();
    const m = fill.match(/^url\(["']?#([^"')]+)["']?\)/);
    const node = m && (el.ownerSVGElement?.getElementById?.(m[1]) || document.getElementById(m[1]));
    return node?.tagName?.toLowerCase() === "pattern" ? node : null;
}

/** true when el is a pattern-filled leaf — what this module expands */
export function hasPatternFill(el) {
    return !!el && el.tagName?.toLowerCase() !== "g" && !!patternOf(el);
}

function chainOf(pattern) {
    const chain = [], seen = new Set();
    let node = pattern;
    while (node && !seen.has(node)) {
        chain.push(node); seen.add(node);
        const ref = node.getAttribute("href") || node.getAttributeNS("http://www.w3.org/1999/xlink", "href") || node.getAttribute("xlink:href") || "";
        node = ref.startsWith("#") ? (node.ownerSVGElement?.getElementById?.(ref.slice(1)) || document.getElementById(ref.slice(1))) : null;
    }
    return chain;
}
const attrOf = (chain, name) => { for (const n of chain) { const v = n.getAttribute(name); if (v != null) return v; } return null; };
const tileChildren = (chain) => { for (const n of chain) { const kids = [...n.children].filter(c => GRAPHIC.has(c.tagName.toLowerCase())); if (kids.length) return kids; } return []; };

/** The product of an SVGTransformList as a DOMMatrix. */
function listMatrix(list) {
    let m = new DOMMatrix();
    if (!list) return m;
    for (let i = 0; i < list.numberOfItems; i++) {
        const t = list.getItem(i).matrix;
        m = m.multiply(new DOMMatrix([t.a, t.b, t.c, t.d, t.e, t.f]));
    }
    return m;
}
const matrixAttr = (m) => `matrix(${[m.a, m.b, m.c, m.d, m.e, m.f].map(v => +v.toFixed(4)).join(" ")})`;

function boxThrough(box, m) {
    const pts = [[box.x, box.y], [box.x + box.width, box.y], [box.x, box.y + box.height], [box.x + box.width, box.y + box.height]]
        .map(([x, y]) => m.transformPoint(new DOMPoint(x, y)));
    const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
    return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}

function insideShape(el, x, y) {
    try { if (typeof el.isPointInFill === "function") return el.isPointInFill(new DOMPoint(x, y)); } catch { /* not a geometry element */ }
    const b = el.getBBox();
    return x >= b.x && x <= b.x + b.width && y >= b.y && y <= b.y + b.height;
}

function uniqueId(svgRoot, wanted) {
    let id = wanted, n = 2;
    while (svgRoot.querySelector(`#${CSS.escape(id)}`) || document.getElementById(id)) id = `${wanted}-${n++}`;
    return id;
}

/**
 * Expand every pattern-filled leaf that carries a propagate() id into a
 * member group carrying that id. Idempotent: a shape already expanded is
 * left alone. Returns the number of shapes expanded.
 */
export function expandPatternFills(svgRoot) {
    if (!svgRoot) return 0;
    let count = 0;
    for (const host of Array.from(svgRoot.querySelectorAll(PROPAGATE_SELECTOR))) {
        try {
            if (host._oscillaPatternGroup || String(host.tagName || "").toLowerCase() === "g") continue;
            const pattern = patternOf(host);
            if (!pattern) continue;                               // a lone element: propagate() will retire it
            if (expandOne(svgRoot, host, pattern)) count++;
        } catch (err) { console.warn(`[pattern] could not expand ${String(host.id).slice(0, 40)}…:`, err); }
    }
    if (count) console.info(`[pattern] expanded ${count} pattern fill${count === 1 ? "" : "s"} into propagate() members`);
    return count;
}

function expandOne(svgRoot, host, pattern) {
    const chain = chainOf(pattern);
    const kids = tileChildren(chain);
    const w = parseFloat(attrOf(chain, "width")) || 0, h = parseFloat(attrOf(chain, "height")) || 0;
    if (!kids.length || w <= 0 || h <= 0) { console.warn("[pattern] pattern has no objects or no tile size"); return false; }
    if ((attrOf(chain, "patternUnits") || "objectBoundingBox") !== "userSpaceOnUse") { console.warn("[pattern] only patternUnits=userSpaceOnUse is supported"); return false; }
    const P = listMatrix(chain.find(n => n.hasAttribute("patternTransform"))?.patternTransform?.baseVal);
    const Pinv = P.inverse();
    if (!Number.isFinite(Pinv.a)) return false;

    // The template is normally in the host's data-oscilla; older files have it
    // in the id. The generated group takes it in whichever form it was found,
    // so propagate() picks the group up on its own terms.
    const src = readPropagateSource(host);
    if (!src) return false;
    const templateId = src.template;
    const uidMatch = templateId.match(/uid\s*:\s*([A-Za-z0-9_-]+)/);
    const prefix = uniqueId(svgRoot, `pattern-${uidMatch ? uidMatch[1] : "host"}`);
    // A host whose template is in data-oscilla keeps the id the file knows it
    // by — that id is how the editor saves the template back.
    const hostId = src.fromId ? prefix : (host.getAttribute("id") || prefix);
    const bb = host.getBBox();                                  // the shape's own space, before its transform
    const box = boxThrough({ x: bb.x, y: bb.y, width: bb.width, height: bb.height }, Pinv);
    const i0 = Math.floor(box.x0 / w) - 1, i1 = Math.ceil(box.x1 / w) + 1;
    const j0 = Math.floor(box.y0 / h) - 1, j1 = Math.ceil(box.y1 / h) + 1;
    const trim = (host.getAttribute("data-pattern-trim") || "clip").toLowerCase();

    const group = document.createElementNS(SVG_NS, "g");
    group.setAttribute("data-oscilla-pattern-of", hostId);
    const t = host.getAttribute("transform"); if (t) group.setAttribute("transform", t);
    host.after(group);                                          // same parent, same stacking, just above the shape

    // Every copy of a tile shape has the same local box, only its transform
    // differs, so each shape is measured ONCE (a probe copy in the group,
    // then removed) and the lattice is culled by arithmetic. Measuring each
    // copy after inserting it would force a layout per dot.
    const kidBox = new Map();
    for (const kid of kids) {
        const probe = kid.cloneNode(true);
        probe.removeAttribute("transform");
        group.appendChild(probe);
        try { const b = probe.getBBox(); kidBox.set(kid, { x: b.x, y: b.y, width: b.width, height: b.height }); } catch { kidBox.set(kid, null); }
        probe.remove();
    }
    let n = 0;
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
        const tile = P.multiply(new DOMMatrix().translate(i * w, j * h));
        for (const kid of kids) {
            const cb = kidBox.get(kid);
            const M = tile.multiply(listMatrix(kid.transform?.baseVal));
            const kb = cb ? boxThrough(cb, M) : null;
            const out = !kb || kb.x1 < bb.x || kb.x0 > bb.x + bb.width || kb.y1 < bb.y || kb.y0 > bb.y + bb.height
                || (trim === "inside" && !insideShape(host, (kb.x0 + kb.x1) / 2, (kb.y0 + kb.y1) / 2));
            if (out) continue;
            const copy = kid.cloneNode(true);
            copy.setAttribute("transform", matrixAttr(M));
            for (const el of [copy, ...copy.querySelectorAll("*")]) { el.removeAttribute("id"); el.removeAttribute("inkscape:collect"); }
            copy.setAttribute("id", `${hostId}-p${++n}`);
            group.appendChild(copy);
        }
    }
    if (!n) { group.remove(); console.warn(`[pattern] no pattern objects fall on ${templateId.slice(0, 40)}…`); return false; }

    if (trim === "clip") {
        let defs = svgRoot.querySelector("defs");
        if (!defs) { defs = document.createElementNS(SVG_NS, "defs"); svgRoot.prepend(defs); }
        const clip = document.createElementNS(SVG_NS, "clipPath");
        clip.setAttribute("id", `${hostId}-clip`);
        clip.setAttribute("clipPathUnits", "userSpaceOnUse");
        const shape = host.cloneNode(false);
        shape.removeAttribute("id"); shape.removeAttribute("transform"); shape.removeAttribute("data-oscilla");
        shape.setAttribute("style", "fill:#000;stroke:none");
        clip.appendChild(shape);
        defs.appendChild(clip);
        group.setAttribute("clip-path", `url(#${hostId}-clip)`);
    }

    // the group takes the template, the shape keeps/gets a plain id and loses
    // its fill for the session
    host.setAttribute("id", hostId);
    host.setAttribute("data-oscilla-pattern-host", "true");
    host.style.fill = "none";
    if (src.fromId) {
        group.setAttribute("id", templateId);        // older file: still an id
    } else {
        group.setAttribute("id", uniqueId(svgRoot, `${prefix}-members`));
        group.setAttribute("data-oscilla", templateId);
        if (src.rest) host.setAttribute("data-oscilla", src.rest);
        else host.removeAttribute("data-oscilla");
    }
    host._oscillaPatternGroup = group;
    group._oscillaPatternHost = host;
    console.info(`[pattern] ${hostId}: ${n} objects from #${pattern.id} for ${templateId.slice(0, 50)}…`);
    return true;
}

